import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BotaoDuplo from '../../../Componentes/BotaoDuplo';
import CampoEdicoes from '../../../Componentes/CampoEdicoes';
import CheckBoxOpcoes from '../../../Componentes/CheckBoxOpcoes';
import CorpoPagina from '../../../Componentes/CorpoPagina';
import ModalConfirmacao from '../../../Componentes/ModalConfirmacao';
import SelectInput from '../../../Componentes/SelectInput';
import TextInput from '../../../Componentes/TextInput';
import TopicoTexto from '../../../Componentes/TopicoTexto';
import { apiService } from '../../../Services/ApiService';
import './DesumidificadorCadastro.css';
import { useTranslation } from 'react-i18next';

export default function DesumidificadorCadastro() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { get, post, put } = apiService();
  const [dehumidifier, setDehumidifier] = useState({
    name: '',
    dehumidifierId: '',
    environment: {
      id: '',
      name: '',
    },
    maintenanceCycle: {
      monthly: false,
      biMonthly: false,
      triMonthly: false,
      semiAnnual: false,
      annual: false,
    },
  });
  const { t } = useTranslation('errors');
  const [isLoading, setIsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    name: true,
    dehumidifierId: true,
    environment: true,
  });
  const [touchedFields, setTouchedFields] = useState({
    name: false,
    dehumidifierId: false,
    environment: false,
  });

  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalCancel, setModalCancel] = useState(false);
  const [modalSuccess, setModalSuccess] = useState(false);
  const [modalError, setModalError] = useState({
    visible: false,
    message: '',
  });
  const [listaAmbientes, setListaAmbientes] = useState([]);
  const [ambiente, setAmbiente] = useState({ value: '', label: 'Selecione' });
  const [maintenanceCycle, setMaintenanceCycle] = useState([
    { value: 'monthly', nome: 'Mensal', state: false },
    { value: 'biMonthly', nome: 'Bimestral', state: false },
    { value: 'triMonthly', nome: 'Trimestral', state: false },
    { value: 'semiAnnual', nome: 'Semestral', state: false },
    { value: 'annual', nome: 'Anual', state: false },
  ]);

  const validators = {
    name: (value) => value && value.trim().length >= 3 && value.trim().length <= 100,
    dehumidifierId: (value) => value && value.trim().length >= 3 && value.trim().length <= 25,
    environment: (value) => value.id,
  };

  const validateField = (name, value) => {
    return validators[name] ? validators[name](value) : true;
  };

  const setAndValidateField = (name, value) => {
    setDehumidifier((prev) => ({ ...prev, [name]: value }));
    const isValid = validateField(name, value);
    setValidationErrors((prev) => ({ ...prev, [name]: !isValid }));
  };

  const handleChange = (name, value) => {
    setAndValidateField(name, value);
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
  };

  useEffect(() => {
    if (id) {
      fetchDehumidifier();
    } else {
      setIsLoading(false);
    }
    fetchAmbientes();
  }, [id]);

  useEffect(() => {
    setIsFormValid(Object.values(validationErrors).every((v) => !v));
  }, [validationErrors]);

  const fetchDehumidifier = async () => {
    setIsLoading(true);
    try {
      const data = await get(`dehumidifiers/${id}`);
      if (data) {
        setDehumidifier({
          name: data.name,
          dehumidifierId: data.dehumidifierId,
          environment: {
            id: data.environment.id,
            name: data.environment.name,
          },
          maintenanceCycle: maintenanceCycle.map((item) => {
            return { ...item, state: data.maintenanceCycle[item.value] };
          }),
        });
        setAmbiente({ value: data.environment.id, label: data.environment.name });
        setMaintenanceCycle(
          maintenanceCycle.map((item) => {
            return { ...item, state: data.maintenanceCycle[item.value] };
          }),
        );

        Object.entries(data).forEach(([key, value]) => {
          setAndValidateField(key, value);
          setTouchedFields((prev) => ({ ...prev, [key]: true }));
        });
      }
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao buscar os detalhes do desumidificador' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAmbientes = async () => {
    try {
      const data = await get('environments/all');
      if (data && data.length) {
        const environments = data.map((item) => {
          return { value: item.id, label: item.name };
        });
        setListaAmbientes(environments);
      }
    } catch (error) {
      setModalError({ visible: true, message: 'Erro ao buscar os ambientes' });
    }
  };

  const handleSelectChange = (label, value) => {
    setAmbiente({ value, label });
    setAndValidateField('environment', { id: value, name: label });
    setTouchedFields((prev) => ({ ...prev, environment: true }));
  };

  const handleSave = async () => {
    setModalConfirm(false);
    if (!isFormValid) {
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        ...dehumidifier,
        environmentId: ambiente.value,
        maintenanceCycle: maintenanceCycle.reduce((acc, item) => {
          acc[item.value] = item.state;
          return acc;
        }, {}),
      };
      if (id) {
        await put(`dehumidifiers/${id}`, payload);
      } else {
        await post('dehumidifiers', payload);
      }
      setModalSuccess(true);
    } catch (error) {
      let errorMessage = 'Erro ao salvar desumidificador';
      if (error.message) {
        errorMessage = t(error.message);
      } else if (error.response && error.response.data && error.response.data.message) {
        errorMessage = t(`errors.${error.response.data.message}`);
      }

      setModalError({
        visible: true,
        message: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckBoxChange = (value) => {
    const updatedCycle = maintenanceCycle.map((item) => {
      if (item.value === value) {
        return { ...item, state: !item.state };
      }
      return item;
    });

    setMaintenanceCycle(updatedCycle);
  };

  return (
    <CorpoPagina loading={isLoading}>
      {modalError.visible && (
        <ModalConfirmacao
          aviso={modalError.message}
          erro={true}
          clicado={() => setModalError({ visible: false, message: '' })}
        />
      )}
      {modalSuccess && (
        <ModalConfirmacao
          aviso="Desumidificador salvo com sucesso!"
          confirmar
          clicado={() => navigate(-1)}
        />
      )}
      {modalConfirm && (
        <ModalConfirmacao
          aviso="Deseja realmente salvar o desumidificador?"
          confirmar={false}
          clicado={handleSave}
          clicadoVolta={() => setModalConfirm(false)}
        />
      )}
      {modalCancel && (
        <ModalConfirmacao
          aviso="Deseja realmente cancelar?"
          confirmar={false}
          clicado={() => navigate(-1)}
          clicadoVolta={() => setModalCancel(false)}
        />
      )}
      <div
        className="ContainerGeral"
        style={{ display: 'flex', flexDirection: 'column', alignContent: 'flex-start' }}
      >
        <TopicoTexto
          titulo={id ? 'Editar Desumidificador' : 'Cadastrar Desumidificador'}
          marginLeft={15}
        />
        <div className="form-row">
          <div className="form-column">
            <CampoEdicoes
              titulo="DADOS GERAIS"
              maxH={505}
              maxW={540}
              width={'100%'}
              height={505}
              minWidth={150}
            >
              <TextInput
                titulo="Nome"
                padrao={dehumidifier.name}
                placeholder="Insira o Nome"
                tipo="input"
                largura="90%"
                top={8}
                onUpdate={(txt) => handleChange('name', txt)}
                erro={touchedFields.name && validationErrors.name}
                erroTexto="Nome inválido"
                maxLength={100}
              />

              <TextInput
                titulo="ID"
                padrao={dehumidifier.dehumidifierId}
                placeholder="Insira o ID"
                tipo="input"
                largura="90%"
                top={8}
                onUpdate={(txt) => handleChange('dehumidifierId', txt)}
                erro={touchedFields.dehumidifierId && validationErrors.dehumidifierId}
                erroTexto="ID inválido"
                maxLength={25}
              />

              <SelectInput
                titulo="Ambiente"
                itens={listaAmbientes}
                padrao={ambiente}
                largura="90%"
                clicado={(txt) => {
                  handleSelectChange(txt.label, txt.value);
                }}
              />
            </CampoEdicoes>
          </div>

          <div className="form-column">
            <CampoEdicoes
              titulo="CICLOS DE MANUTENÇÃO"
              maxH={505}
              maxW={540}
              width={'100%'}
              height={505}
              minWidth={150}
            >
              <CheckBoxOpcoes
                titulo="Selecione o período"
                items={maintenanceCycle}
                top={8}
                width="92%"
                clicado={(txt) => handleCheckBoxChange(txt)}
              />
            </CampoEdicoes>
          </div>
        </div>
        <BotaoDuplo
          nome="Salvar"
          nomeVolta="Cancelar"
          top={16}
          bottom={32}
          clicado={() => setModalConfirm(true)}
          clicadoVolta={() => setModalCancel(true)}
          externo={true}
          disabled={!isFormValid}
        />
      </div>
    </CorpoPagina>
  );
}
